import { useMemo, useState } from 'react';
import { Select } from 'antd';
import type { Study } from '@/models/study';
import { useDebounce } from '@/hooks/useDebounce';
import { useGetStudyQuery } from '@/state/slices/studies';
import { formatStudyDate } from '@/shared/utils/studyDate';
import { useReportStudies } from './hooks/useReportStudies';
import styles from './TemplateEditor.module.css';

const SEARCH_DEBOUNCE_MS = 300;

function studyLabel(study: Study) {
  const name =
    [study.patientLastName, study.patientFirstName]
      .filter(Boolean)
      .join(', ') || 'Unknown patient';
  return `${name} · ${formatStudyDate(study.studyDate)} · ${study.icid}`;
}

interface PreviewStudyPickerProps {
  value: string | undefined;
  onChange: (studyUuid: string) => void;
}

export function PreviewStudyPicker({
  value,
  onChange,
}: PreviewStudyPickerProps) {
  const [search, setSearch] = useState('');
  const { data, isFetching } = useReportStudies(
    useDebounce(search, SEARCH_DEBOUNCE_MS)
  );

  // The selection may be outside this page, e.g. after a search or a reload.
  const isListed = data?.results.some((s) => s.uuid === value);
  const { data: selected } = useGetStudyQuery(
    { uuid: value ?? '' },
    { skip: !value || !data || isListed }
  );

  const options = useMemo(
    () =>
      [
        ...(data?.results ?? []),
        ...(selected && !isListed ? [selected] : []),
      ].map((s) => ({ value: s.uuid, label: studyLabel(s) })),
    [data, selected, isListed]
  );

  return (
    <Select
      showSearch
      filterOption={false}
      onSearch={setSearch}
      value={value}
      onChange={onChange}
      options={options}
      loading={isFetching}
      placeholder="Pick a study to preview"
      notFoundContent={isFetching ? 'Searching…' : 'No studies with a report'}
      className={styles.studyPicker}
      aria-label="Preview study"
    />
  );
}
